/**
 * 결제 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import StatCard from '../../components/dashboard/StatCard';
import { paymentService } from '../../services/paymentService';
import { Payment, PaymentFilter, PaymentStatus, PaymentMethod } from '../../types/payment';
import { format } from 'date-fns';

const PaymentsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PaymentFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['payments', filter],
    queryFn: () => paymentService.getPayments(filter),
  });

  const { data: statistics } = useQuery({
    queryKey: ['payment-statistics', filter],
    queryFn: () => paymentService.getPaymentStatistics({
      date_from: filter.date_from,
      date_to: filter.date_to,
    }),
  });

  const getStatusBadge = (status: PaymentStatus) => {
    const statusMap: Record<PaymentStatus, { variant: string; label: string }> = {
      pending: { variant: 'warning', label: '대기중' },
      completed: { variant: 'success', label: '완료' },
      failed: { variant: 'danger', label: '실패' },
      refunded: { variant: 'info', label: '환불됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getMethodLabel = (method: PaymentMethod) => {
    const methodMap: Record<PaymentMethod, string> = {
      card: '카드',
      qrcode: 'QR코드',
    };
    return methodMap[method] || method;
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  const columns: Column<Payment>[] = [
    {
      key: 'payment_number',
      label: '결제번호',
      render: (payment) => (
        <span style={{ fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
          {payment.payment_number}
        </span>
      ),
    },
    {
      key: 'booking_number',
      label: '예약번호',
      render: (payment) => payment.booking?.booking_number || '-',
    },
    {
      key: 'payment_method',
      label: '결제방법',
      render: (payment) => (
        <div>
          <div>{getMethodLabel(payment.payment_method)}</div>
          {payment.payment_method_detail && (
            <small className="text-muted">{payment.payment_method_detail}</small>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      label: '결제금액',
      render: (payment) => formatCurrency(payment.amount),
    },
    {
      key: 'status',
      label: '상태',
      render: (payment) => getStatusBadge(payment.status),
    },
    {
      key: 'paid_at',
      label: '결제일시',
      render: (payment) => payment.paid_at
        ? format(new Date(payment.paid_at), 'yyyy-MM-dd HH:mm')
        : '-',
    },
    {
      key: 'created_at',
      label: '생성일시',
      render: (payment) => format(new Date(payment.created_at), 'yyyy-MM-dd HH:mm'),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: 'pending', label: '대기중' },
        { value: 'completed', label: '완료' },
        { value: 'failed', label: '실패' },
        { value: 'refunded', label: '환불됨' },
      ],
    },
    {
      name: 'payment_method',
      label: '결제방법',
      type: 'select',
      options: [
        { value: 'card', label: '카드' },
        { value: 'qrcode', label: 'QR코드' },
      ],
    },
    {
      name: 'paid_at',
      label: '결제일',
      type: 'dateRange',
    },
    {
      name: 'payment_number',
      label: '결제번호',
      type: 'text',
      placeholder: '결제번호 검색',
    },
    {
      name: 'booking_number',
      label: '예약번호',
      type: 'text',
      placeholder: '예약번호 검색',
    },
  ];

  const handleFilterChange = (newFilter: Record<string, any>) => {
    setFilter({
      ...filter,
      ...newFilter,
      page: 1,
    });
  };

  const handlePageChange = (page: number) => {
    setFilter({ ...filter, page });
  };

  const handleRowClick = (payment: Payment) => {
    navigate(`/admin/payments/${payment.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="mb-4" style={{ color: '#fff' }}>결제 관리</h1>

        {/* 통계 카드 */}
        {statistics && (
          <Row className="mb-4">
            <Col md={3} className="mb-3">
              <StatCard
                title="총 매출"
                value={statistics.total_revenue}
                subtitle="전체 기간"
                icon="💰"
              />
            </Col>
            <Col md={3} className="mb-3">
              <StatCard
                title="오늘 매출"
                value={statistics.today_revenue}
                subtitle="오늘 기준"
                icon="📊"
              />
            </Col>
            <Col md={3} className="mb-3">
              <StatCard
                title="완료된 결제"
                value={`${statistics.completed_count}건`}
                subtitle={`전체 ${statistics.total_count}건`}
                icon="✅"
              />
            </Col>
            <Col md={3} className="mb-3">
              <StatCard
                title="환불 건수"
                value={`${statistics.refunded_count}건`}
                subtitle="전체 기준"
                icon="↩️"
              />
            </Col>
          </Row>
        )}

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.payments || []}
              loading={isLoading}
              emptyMessage="결제 내역이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PaymentsList;

